import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn } from "../components/common";
import { toast } from "sonner";
import { Trash2, ShieldAlert, RefreshCw } from "lucide-react";

export default function Maintenance() {
  const [targets, setTargets] = useState([]);
  const [selected, setSelected] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/cleanup-targets").then((r) => setTargets(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const toggle = (k) => setSelected((s) => ({ ...s, [k]: !s[k] }));
  const chosen = targets.filter((t) => selected[t.key]).map((t) => t.key);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup", { targets: chosen });
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`${total} registos removidos`);
      setSelected({});
      setConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <PageHeader overline="Super Admin" title="Manutencao de dados" />
      <div className="mb-6 flex items-start gap-3 border border-amber-200 bg-amber-50 rounded-lg p-4">
        <ShieldAlert className="text-amber-600 mt-0.5 shrink-0" size={20} />
        <div className="text-amber-700 text-sm leading-relaxed">
          Estas acoes eliminam dados de forma permanente. <b>Nunca</b> sao afetados: utilizadores, roles, catalogo (plataformas/planos), definicoes/integracoes, nem os dados vindos da CogniEtica (subscricoes via API, tickets do sistema e eventos de webhook).
        </div>
      </div>

      <div className="border border-tint rounded-lg divide-y divide-tint bg-white" data-testid="cleanup-list">
        {targets.map((t) => (
          <label key={t.key} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-rowalt" data-testid={`cleanup-row-${t.key}`}>
            <input type="checkbox" checked={!!selected[t.key]} onChange={() => toggle(t.key)} data-testid={`cleanup-check-${t.key}`} className="w-4 h-4" />
            <span className="flex-1 text-navy font-medium text-sm">{t.label}</span>
            <span className="text-xs bg-tint text-navy px-2.5 py-1 rounded-full font-semibold">{t.count} registos</span>
          </label>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Btn variant="ghost" onClick={load} testid="cleanup-refresh"><RefreshCw size={16} /> Atualizar contagens</Btn>
        <Btn variant="danger" onClick={() => setConfirm(true)} disabled={chosen.length === 0} testid="cleanup-open-confirm">
          <Trash2 size={16} /> Limpar selecionados ({chosen.length})
        </Btn>
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="cleanup-confirm-modal">
          <div className="bg-white rounded-xl p-8 w-full max-w-md">
            <h2 className="text-navy text-xl font-bold tracking-tight mb-2">Confirmar limpeza</h2>
            <p className="text-muted2 text-sm mb-4">Vai eliminar permanentemente:</p>
            <ul className="list-disc pl-5 mb-6 text-sm text-navy space-y-1">
              {targets.filter((t) => selected[t.key]).map((t) => (
                <li key={t.key}>{t.label} <span className="text-muted2">({t.count})</span></li>
              ))}
            </ul>
            <div className="flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setConfirm(false)} testid="cleanup-cancel">Cancelar</Btn>
              <Btn variant="danger" onClick={run} disabled={busy} testid="cleanup-confirm">{busy ? "A limpar..." : "Eliminar definitivamente"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
